import { mapSFCancellationPolicyToAdmin } from '~/utils/mapSFCancelltionPolicyToAdmin';

import type { useOpportunityStep } from './PrerequisitesStep/useOpportunityStep';
import type { usePropertyStep } from './PropertyStep/usePropertyStep';
import {
  mapExtraGuestSurchargeAgeRangesToPropertyFormData,
  mapRedemptionOptionsToPropertyFormData,
  mapVendorBillingToPropertyFormData,
} from './PropertyStep/utils';
import type { useRatePlanStep } from './RatePlanStep/useRatePlanStep';
import { getDefaultFlashRatePlanName } from './RatePlanStep/utils';
import type { useRoomsStep } from './RoomsStep/useRoomsStep';
import { DEFAULT_NEW_PROPERTY_LOGO_ID, STEPS } from './constants';
import type { SetUpFlashOfferSectionProps } from './types';
import { getFlashRatePlanNameFromTravelStart } from './utils';

// What a step's "Next" handler tells handleNext to do:
// - 'blocked': something's still in progress; don't touch the error message or advance.
// - 'error': show `message` (possibly null, to clear a stale one) and don't advance.
// - 'handled': the step handler already set its own error message and active step.
// - 'advance': clear the error message (unless `preserveError`) and move to the next step.
type StepAdvanceResult =
  | { type: 'blocked' }
  | { message: string | null; type: 'error' }
  | { type: 'handled' }
  | { preserveError?: boolean; type: 'advance' };

export interface UseWizardNavigationParams {
  activeStep: number;
  /** Steps before this have already cleared their gate and haven't been edited since. */
  clearedThrough: number;
  isStepBusy: boolean;
  opportunity: ReturnType<typeof useOpportunityStep>;
  propertyStep: ReturnType<typeof usePropertyStep>;
  ratePlanStep: ReturnType<typeof useRatePlanStep>;
  roomsStep: ReturnType<typeof useRoomsStep>;
  setActiveStep: (value: number | ((prev: number) => number)) => void;
  setErrorMessage: (message: string | null) => void;
  setIsAdvancingStep: (isAdvancing: boolean) => void;
  setPropertyWarningMessage: (message: string | null) => void;
  vendor: SetUpFlashOfferSectionProps['vendor'];
  vendorId: string;
}

/**
 * Owns moving the wizard from one step to the next: per-step validation/creation (dispatched by
 * step index, since each branch only ever applies to one specific `activeStep`), and the
 * cross-step data seeding that happens once Prerequisites clears (property form defaults, rate
 * plan defaults, Salesforce room list).
 */
export function useWizardNavigation({
  activeStep,
  clearedThrough,
  isStepBusy,
  opportunity,
  propertyStep,
  ratePlanStep,
  roomsStep,
  setActiveStep,
  setErrorMessage,
  setIsAdvancingStep,
  setPropertyWarningMessage,
  vendor,
  vendorId,
}: UseWizardNavigationParams) {
  async function advanceSalesforceIdStep(): Promise<StepAdvanceResult> {
    const { errorMessage, success } = await opportunity.handleSalesforceIdNext();

    if (!success) {
      return { message: errorMessage ?? null, type: 'error' };
    }

    return { type: 'advance' };
  }

  async function advancePrerequisitesStep(): Promise<StepAdvanceResult> {
    const prerequisitesResult = await opportunity.handlePrerequisitesNext();

    if (!prerequisitesResult.ready) {
      return { type: 'blocked' };
    }

    const { hasExistingPropertyLink, opportunityRelated, validatedOpportunity } = prerequisitesResult;

    setPropertyWarningMessage(
      hasExistingPropertyLink
        ? 'This offer is already associated with a property. Continuing may relink or overwrite its existing package/room/rate assignments.'
        : null,
    );

    const cancellationPolicy =
      mapSFCancellationPolicyToAdmin(validatedOpportunity.functional_details?.cancellation_policy__c ?? undefined) ??
      '';
    const travelStartRatePlanName = getFlashRatePlanNameFromTravelStart(opportunityRelated?.deals);

    ratePlanStep.initializeRatePlanDrafts({
      ratePlanCancellationPolicy: cancellationPolicy,
      ratePlanName: travelStartRatePlanName || getDefaultFlashRatePlanName(),
    });

    propertyStep.setPropertyFormData({
      id_salesforce_external: vendorId,
      ...mapVendorBillingToPropertyFormData(vendor),
      logo_id_cloudinary_external: DEFAULT_NEW_PROPERTY_LOGO_ID,
      ...mapRedemptionOptionsToPropertyFormData(validatedOpportunity.key_commercial?.redemption_options__c),
      ...mapExtraGuestSurchargeAgeRangesToPropertyFormData(opportunityRelated?.extra_guest_surcharges),
    });
    roomsStep.seedFromOpportunity(opportunityRelated?.deals ?? opportunityRelated?.rooms ?? []);

    return { type: 'advance' };
  }

  async function advancePropertyStep(): Promise<StepAdvanceResult> {
    if (propertyStep.isCreatingNewProperty) {
      return { type: 'blocked' };
    }

    if (propertyStep.isLoadingPropertyForm) {
      return { message: 'Wait for property details to finish loading.', type: 'error' };
    }

    if (propertyStep.missingRequiredPropertyFields.length > 0) {
      return {
        message: `Complete mandatory property fields: ${propertyStep.missingRequiredPropertyFields.join(', ')}.`,
        type: 'error',
      };
    }

    if (propertyStep.isCreatingProperty && !propertyStep.propertySchema) {
      return { message: 'Unable to validate property details.', type: 'error' };
    }

    // Creating a new property submits via its own form (see WizardActions) and never reaches
    // this handler -- this branch only runs when selecting an existing property.
    if (propertyStep.isCreatingProperty) {
      return { type: 'advance' };
    }

    setIsAdvancingStep(true);
    let hasPrepareWarning = false;

    try {
      const { warningMessage } = await roomsStep.prepareRoomDrafts(
        propertyStep.selectedProperty,
        propertyStep.selectedPropertyRoomTypes,
      );

      if (warningMessage) {
        hasPrepareWarning = true;
        setErrorMessage(warningMessage);
      }
    } finally {
      setIsAdvancingStep(false);
    }

    return { preserveError: hasPrepareWarning || !propertyStep.selectedPropertyHasRooms, type: 'advance' };
  }

  // Only steps 0, 1 and 2 dispatch through here -- Rooms (3), Rate Plan (4) and Room Rates (5)
  // are pure local drafting now (nothing to create until Submission, and their own
  // isXStepNextDisabled flags already gate the Next button), Property (when creating new),
  // Taxes & Fees, and Package Setup submit via their own form (see WizardActions'
  // `submitsCurrentStep`), and the remaining steps (Capacity, Content) have nothing to validate
  // before advancing.
  const stepAdvanceHandlers: Record<number, () => Promise<StepAdvanceResult>> = {
    0: advanceSalesforceIdStep,
    1: advancePrerequisitesStep,
    2: advancePropertyStep,
  };

  async function handleNext() {
    if (isStepBusy) {
      return;
    }

    // A step that already cleared its gate (and hasn't been edited since) just moves on: re-running
    // it would redo one-off seeding, e.g. overwriting the CM's rate plan and property edits.
    const advanceStep = activeStep < clearedThrough ? undefined : stepAdvanceHandlers[activeStep];
    const result = advanceStep ? await advanceStep() : { type: 'advance' as const };

    if (result.type === 'blocked' || result.type === 'handled') {
      return;
    }

    if (result.type === 'error') {
      setErrorMessage(result.message);
      return;
    }

    if (!result.preserveError) {
      setErrorMessage(null);
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  return { handleNext };
}
